import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PendingIncomeRow } from '@econtractor/types';
import * as apiWarehouse from 'api/apiWarehouses';
import { useAuth } from 'context/AuthContext';
import { useToast } from 'context/ToastContext';
import { canConfirmPendingIncome, type PendingWarehouseInfo } from '../helper/canConfirmPending';

interface PendingResult {
    stat?: boolean;
    errCode?: string | null;
}

interface UsePendingIncomeParams {
    wId: number;
    warehouseInfo: PendingWarehouseInfo | null;
    refreshVersion: number;
    onChanged: () => Promise<unknown> | unknown;
}

const usePendingIncome = ({ wId, warehouseInfo, refreshVersion, onChanged }: UsePendingIncomeParams) => {
    const { t } = useTranslation(['Warehouses', 'common']);
    const { auth } = useAuth();
    const { showToast } = useToast();
    const [rows, setRows] = useState<PendingIncomeRow[] | null>(null);
    const [saving, setSaving] = useState(false);
    const [selected, setSelected] = useState<number[]>([]);

    const canAct = canConfirmPendingIncome(auth?.user, wId, warehouseInfo);

    const fetchPending = useCallback(async () => {
        const res = await apiWarehouse.getPendingIncome(wId);
        const next = Array.isArray(res?.rows) ? (res.rows as PendingIncomeRow[]) : [];
        setRows(next);
        setSelected((prev) => prev.filter((id) => next.some((row) => row.ID === id)));
    }, [wId]);

    useEffect(() => {
        setRows(null);
        setSelected([]);
    }, [wId]);

    useEffect(() => {
        if (!refreshVersion) return;
        void fetchPending();
    }, [fetchPending, refreshVersion]);

    const finish = useCallback(async (result: PendingResult | null, okMessage: string) => {
        if (result?.stat) {
            showToast(okMessage, 'success');
            await fetchPending();
            await onChanged();
            return;
        }
        if (result?.errCode === 'NO_PRODUCT_STOCK') {
            showToast(t('messNoProductsStock'), 'danger');
            return;
        }
        showToast(t('common:messError'), 'danger');
    }, [fetchPending, onChanged, showToast, t]);

    const run = useCallback(async (action: () => Promise<PendingResult | null>, okMessage: string) => {
        if (saving || !canAct) return;
        setSaving(true);
        try {
            await finish(await action(), okMessage);
        } finally {
            setSaving(false);
        }
    }, [canAct, finish, saving]);

    const acceptRows = useCallback((items: { operationId: number; count?: number }[]) => {
        void run(
            () => apiWarehouse.acceptPendingIncome(wId, items),
            t('pendingIncome.messAccepted'),
        );
    }, [run, t, wId]);

    const rejectRows = useCallback((operationIds: number[]) => {
        void run(
            () => apiWarehouse.rejectPendingIncome(wId, operationIds),
            t('pendingIncome.messRejected'),
        );
    }, [run, t, wId]);

    const toggle = (operationId: number) => {
        setSelected((prev) => (
            prev.includes(operationId)
                ? prev.filter((id) => id !== operationId)
                : [...prev, operationId]
        ));
    };

    const allSelected = useMemo(
        () => Boolean(rows?.length) && rows!.every((row) => selected.includes(row.ID)),
        [rows, selected],
    );

    const toggleAll = () => {
        if (!rows?.length) return;
        setSelected(allSelected ? [] : rows.map((row) => row.ID));
    };

    return {
        rows,
        saving,
        canAct,
        selected,
        allSelected,
        toggle,
        toggleAll,
        acceptRows,
        rejectRows,
    };
};

export default usePendingIncome;
